class GltheaaHeroSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.progressBar = null;
    this.progressEl = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onSectionLoad = this.onSectionLoad.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
  }

  connectedCallback() {
    if (typeof Swiper === 'undefined') {
      window.addEventListener('load', () => this.init(), { once: true });
    } else {
      this.init();
    }

    this.reducedMotion.addEventListener('change', this.onReducedMotionChange);

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }
  }

  disconnectedCallback() {
    this.destroy();
    this.reducedMotion.removeEventListener('change', this.onReducedMotionChange);

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }
  }

  onSectionLoad(event) {
    if (event.detail.sectionId === this.dataset.sectionId) {
      this.init();
    }
  }

  onBlockSelect(event) {
    if (event.detail.sectionId !== this.dataset.sectionId || !this.swiper) return;

    const blockId = event.detail.blockId;
    const slide = this.querySelector(`#shopify-block-${blockId}`);

    if (!slide) return;

    const slideIndex = this.getSlideIndex(slide);
    if (slideIndex >= 0) {
      if (this.swiper.params.loop) {
        this.swiper.slideToLoop(slideIndex);
      } else {
        this.swiper.slideTo(slideIndex);
      }
      this.syncMedia(slide);
    }
  }

  onReducedMotionChange() {
    if (!this.swiper?.autoplay) return;

    if (this.reducedMotion.matches) {
      this.swiper.autoplay.stop();
      this.toggleProgress(false);
    } else if (this.dataset.autoplay === 'true') {
      this.swiper.autoplay.start();
      this.toggleProgress(Number(this.dataset.autoplaySpeed || 5) > 1);
      this.resetProgress();
    }
  }

  init() {
    if (typeof Swiper === 'undefined') return;

    const slideCount = Number(this.dataset.slideCount || 0);
    if (slideCount < 1) return;

    this.destroy();

    const swiperElement = this.querySelector('.swiper');
    if (!swiperElement) return;

    this.progressEl = this.querySelector('.gltheaa-hero-slider__progress');
    this.progressBar = this.querySelector('.gltheaa-hero-slider__progress-bar');

    const effect = this.dataset.effect === 'fade' ? 'fade' : 'slide';
    const loop = this.dataset.loop === 'true' && slideCount > 1;
    const autoplayEnabled = this.dataset.autoplay === 'true' && slideCount > 1 && !this.reducedMotion.matches;
    const autoplaySpeed = Number(this.dataset.autoplaySpeed || 5) * 1000;
    const pauseOnHover = this.dataset.pauseOnHover === 'true';
    const paginationEl = this.querySelector('.gltheaa-hero-slider__pagination');
    const showProgress = autoplayEnabled && autoplaySpeed > 1000;
    const transitionSpeed = this.getTransitionSpeed(autoplayEnabled, autoplaySpeed);

    this.toggleProgress(showProgress);

    const config = {
      effect,
      loop,
      speed: transitionSpeed,
      slidesPerView: 1,
      watchOverflow: true,
      navigation: {
        nextEl: this.querySelector('.gltheaa-hero-slider__nav--next'),
        prevEl: this.querySelector('.gltheaa-hero-slider__nav--prev'),
      },
      on: {
        init: () => {
          this.syncCta();
          this.syncMedia();
          this.resetProgress();
        },
        slideChangeTransitionEnd: () => {
          this.resetProgress();
        },
        autoplayPause: () => {
          this.pauseProgress();
        },
        autoplayResume: () => {
          this.resumeProgress();
        },
        autoplayStop: () => {
          this.pauseProgress();
        },
        autoplayStart: () => {
          this.resetProgress();
        },
        slideChange: () => {
          this.syncCta();
          this.syncMedia();
        },
      },
    };

    if (effect === 'fade') {
      config.fadeEffect = { crossFade: true };
    }

    if (paginationEl && this.dataset.showPagination === 'true') {
      config.pagination = {
        el: paginationEl,
        clickable: true,
      };
    }

    if (autoplayEnabled) {
      config.autoplay = {
        delay: autoplaySpeed,
        disableOnInteraction: false,
        pauseOnMouseEnter: pauseOnHover,
      };
    }

    this.swiper = new Swiper(swiperElement, config);
  }

  destroy() {
    if (!this.swiper) return;
    this.pauseAllMedia();
    this.swiper.destroy(true, true);
    this.swiper = null;
    this.pauseProgress();
  }

  getTransitionSpeed(autoplayEnabled, autoplaySpeed) {
    if (!autoplayEnabled) return 700;
    if (autoplaySpeed > 1000) return 700;
    return Math.max(60, Math.round(autoplaySpeed * 0.4));
  }

  toggleProgress(visible) {
    if (!this.progressEl) return;
    this.progressEl.hidden = !visible;
  }

  resetProgress() {
    if (!this.progressBar || this.progressEl?.hidden) return;

    this.progressBar.style.animation = 'none';
    void this.progressBar.offsetWidth;
    this.progressBar.style.removeProperty('animation');
    this.progressBar.style.animationPlayState = 'running';
  }

  pauseProgress() {
    if (!this.progressBar || this.progressEl?.hidden) return;
    this.progressBar.style.animationPlayState = 'paused';
  }

  resumeProgress() {
    if (!this.progressBar || this.progressEl?.hidden) return;
    this.progressBar.style.animationPlayState = 'running';
  }

  getSlideIndex(slideElement) {
    const slides = Array.from(this.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)'));
    return slides.indexOf(slideElement);
  }

  getActiveSlide() {
    return this.querySelector('.swiper-slide-active');
  }

  syncCta() {
    if (!this.swiper) return;

    const activeIndex = this.swiper.realIndex;
    this.querySelectorAll('.gltheaa-hero-slider__cta-wrap[data-slide-index]').forEach((wrap) => {
      const isActive = Number(wrap.dataset.slideIndex) === activeIndex;
      wrap.toggleAttribute('hidden', !isActive);
    });
  }

  syncMedia(activeSlide = this.getActiveSlide()) {
    this.pauseAllMedia(activeSlide);
    if (!activeSlide) return;

    const video = activeSlide.querySelector('video.gltheaa-hero-slider__video');
    if (video) {
      const poster = activeSlide.querySelector('.gltheaa-hero-slider__poster--custom');
      video.play().catch(() => {});
      if (poster) poster.classList.add('is-hidden');
      return;
    }

    const iframe = activeSlide.querySelector('iframe.gltheaa-hero-slider__iframe');
    if (iframe) {
      this.activateIframe(iframe, activeSlide);
    }
  }

  pauseAllMedia(exceptSlide = null) {
    this.querySelectorAll('video.gltheaa-hero-slider__video').forEach((video) => {
      const slide = video.closest('.swiper-slide');
      if (slide && slide !== exceptSlide) {
        video.pause();
        const poster = slide.querySelector('.gltheaa-hero-slider__poster--custom');
        if (poster) poster.classList.remove('is-hidden');
      }
    });

    this.querySelectorAll('iframe.gltheaa-hero-slider__iframe').forEach((iframe) => {
      const slide = iframe.closest('.swiper-slide');
      if (slide && slide !== exceptSlide) {
        this.deactivateIframe(iframe, slide);
      }
    });
  }

  activateIframe(iframe, slide) {
    const poster = slide.querySelector('.gltheaa-hero-slider__poster');
    const dataSrc = iframe.getAttribute('data-src');

    if (!iframe.getAttribute('src') && dataSrc) {
      const autoplaySrc = dataSrc.replace('autoplay=0', 'autoplay=1');
      iframe.setAttribute('src', autoplaySrc);
    }

    if (poster) {
      poster.classList.add('is-hidden');
    }
  }

  deactivateIframe(iframe, slide) {
    const poster = slide.querySelector('.gltheaa-hero-slider__poster');
    iframe.removeAttribute('src');

    if (poster) {
      poster.classList.remove('is-hidden');
    }
  }
}

customElements.define('gltheaa-hero-slider', GltheaaHeroSlider);
